import React, {useCallback} from 'react';

import CSSModules from 'react-css-modules';

import {SliderHandle} from './SliderHandle';

import styles from './styles.module.scss';

// Minimum gap between start and end handles (in seconds)
const MIN_HANDLE_GAP = 1;

type VideoTrimSliderProps = {
  videoStart: number;
  videoEnd: number;
  videoDuration: number;
  setVideoStart: (start: number) => void;
  setVideoEnd: (end: number) => void;
};

const VideoTrimSliderComponent = ({
  videoStart,
  videoEnd,
  videoDuration,
  setVideoStart,
  setVideoEnd,
}: VideoTrimSliderProps) => {
  const minAllowedEnd = videoStart + MIN_HANDLE_GAP;
  const maxAllowedStart = videoEnd - MIN_HANDLE_GAP;

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      const clickTime = clickPosition * videoDuration;

      // Use midpoint to determine which handle to move
      const midPoint = (videoStart + videoEnd) / 2;

      if (clickTime < midPoint) {
        setVideoStart(Math.max(0, Math.min(maxAllowedStart, clickTime)));
      } else {
        setVideoEnd(
          Math.min(videoDuration, Math.max(minAllowedEnd, clickTime))
        );
      }
    },
    [
      videoStart,
      videoEnd,
      videoDuration,
      setVideoStart,
      setVideoEnd,
      maxAllowedStart,
      minAllowedEnd,
    ]
  );

  return (
    <div styleName='range-slider-container'>
      <div styleName='slider-track' onClick={handleTrackClick} />
      <div
        styleName='slider-active-track'
        style={{
          left: `${(videoStart / videoDuration) * 100}%`,
          width: `${((videoEnd - videoStart) / videoDuration) * 100}%`,
        }}
      />
      <SliderHandle
        isStartHandle={true}
        value={videoStart}
        videoDuration={videoDuration}
        onValueChange={(newValue) => {
          setVideoStart(Math.min(maxAllowedStart, newValue));
        }}
        minValue={0}
        maxValue={videoEnd - MIN_HANDLE_GAP}
      />
      <SliderHandle
        isStartHandle={false}
        value={videoEnd}
        videoDuration={videoDuration}
        onValueChange={(newValue) => {
          setVideoEnd(Math.max(minAllowedEnd, newValue));
        }}
        minValue={videoStart + MIN_HANDLE_GAP}
        maxValue={videoDuration}
      />
    </div>
  );
};

export const VideoTrimSlider = CSSModules(VideoTrimSliderComponent, styles, {
  allowMultiple: true,
});
